import { useRef } from "react";
import { motion, useInView } from "motion/react";
import { useLanguage } from "../../i18n/LanguageContext";
import { translations } from "../../i18n/translations";

const silkEase = [0.22, 1, 0.36, 1] as const;

const sectionVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: silkEase } },
};

export function Steps() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.15 });

  const { language } = useLanguage();
  const t = translations[language].steps;

  return (
    <section
      className="py-24 px-6"
      style={{
        background: "var(--surface-1)",
        borderTop: "1px solid var(--border)",
      }}
    >
      <motion.div
        ref={sectionRef}
        className="max-w-5xl mx-auto"
        variants={sectionVariants}
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
      >
        <motion.h2
          className="text-3xl md:text-4xl mb-14 text-center"
          style={{ color: "var(--foreground)", fontFamily: "var(--font-display)" }}
          variants={itemVariants}
        >
          {t.heading}
        </motion.h2>

        <div className="relative grid md:grid-cols-3 gap-6">
          {/* Connecting line between the steps */}
          <div
            className="hidden md:block absolute top-7 left-[16%] right-[16%] h-px"
            style={{
              background:
                "linear-gradient(90deg, transparent, rgba(212,149,106,0.35), transparent)",
            }}
          />

          {t.items.map((item, i) => (
            <motion.div
              key={i}
              className="relative rounded-2xl p-6 text-center"
              style={{
                border: "1px solid var(--border)",
                background: "var(--surface-2)",
              }}
              variants={itemVariants}
              whileHover={{
                y: -4,
                borderColor: "var(--border-strong)",
                transition: { duration: 0.25, ease: "easeOut" },
              }}
            >
              <span
                className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-5 text-lg"
                style={{
                  background: "var(--surface-1)",
                  border: "1px solid var(--border-strong)",
                  color: "var(--gold)",
                  fontFamily: "var(--font-mono)",
                }}
              >
                {i + 1}
              </span>
              <h3
                className="text-lg mb-2"
                style={{ fontFamily: "var(--font-display)", color: "var(--cream)" }}
              >
                {item.title}
              </h3>
              <p
                className="text-sm leading-relaxed"
                style={{ color: "var(--foreground-muted)", fontFamily: "var(--font-body)" }}
              >
                {item.description}
              </p>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </section>
  );
}
